import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classNames from 'clsx';

import { TOUCH_SENSITIVE_CAROUSEL } from '../../../common/constants';
import { channelPropTypes } from '../../../common/constants/propTypes';
import { DESKTOP_TYPE_PADDING_DIVIDER, MOBILE_TYPE_PADDING_DIVIDER } from '../../constants/carousels';
import breakpoints from '@/mui/constants/breakpoints';

import { getMainWidth, IsInPopupContext, RootContainers } from '../../../common/helpers';
import { getNumberInRange, isInRange } from '../../../common/helpers/number';
import * as commonSelectors from '../../../common/redux/selectors/common';
import { slideDimensions } from '../../helpers/carousel';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import Arrow from './Arrow/Arrow';
import CarouselSkeleton from './CarouselSkeleton/CarouselSkeleton';
import Slide from './Slide/Slide';

import styles from './Carousel.module.scss';

const KEY_TAB = 'Tab';
const TAB_PRESS_DELAY_TIME = 100;

const CarouselWidget = forwardRef(
  ({ activeSlideMediaId = '', onSlideClickFn = () => null, onSlideDoubleClickFn = () => null, ...props }, ref) => {
    const isInPopup = useContext(IsInPopupContext);
    const popupSize = useSelector(commonSelectors.popupSizeSelector);

    const wrapperRef = useRef(null);
    const contentRef = useRef(null);
    const tabPressDelay = useRef(null);

    const getDimension = (width) => slideDimensions(props.channel.design, props.channel.format, width);

    const [indexPosition, setIndexPosition] = useState(0);
    const [translateShift, setTranslateShift] = useState(0);
    const [start, setStart] = useState({ x: 0, y: 0, canceled: false });
    const [draggableShiftPosition, setDraggableShiftPosition] = useState(0);
    const [wrapperWidth, setWrapperWidth] = useState(getMainWidth(isInPopup, popupSize));
    const [dimensions, setDimensions] = useState(getDimension(wrapperWidth));

    const [prevButton, setPrevState] = useState(false);
    const [nextButton, setNextState] = useState(false);

    const [animated, setAnimatedState] = useState(true);

    const [padding, setPadding] = useState(
      wrapperWidth <= breakpoints.values.x2l ? MOBILE_TYPE_PADDING_DIVIDER : DESKTOP_TYPE_PADDING_DIVIDER,
    );

    const shiftSize = dimensions.width + padding;

    const children = props.channel.playlist;

    const carouselWidth = children.length * shiftSize - padding;
    const slidesToScroll = Math.max(1, Math.floor(wrapperWidth / shiftSize));
    const indexStart = Math.max(0, Math.floor(Math.abs(translateShift) / shiftSize));
    const visibleIndexStart = Math.max(0, indexStart - slidesToScroll);
    const visibleIndexEnd = indexStart + slidesToScroll + slidesToScroll;

    const changeSlide = (delta) => {
      const width = children.length - Math.floor(wrapperWidth / shiftSize);

      const nextIndex = getNumberInRange(indexPosition + slidesToScroll * delta, 0, width);

      setAnimatedState(true);
      setIndexPosition(nextIndex);
    };

    const isDraggable = isMobileUserAgent && carouselWidth > wrapperWidth;

    useEffect(() => {
      const parentSubscription = isInPopup ? window : RootContainers.getRootContainer();

      const fn = () => {
        const width = getMainWidth(isInPopup, popupSize);

        setPadding(width <= breakpoints.values.x2l ? MOBILE_TYPE_PADDING_DIVIDER : DESKTOP_TYPE_PADDING_DIVIDER);
        if (wrapperRef.current) {
          setWrapperWidth(wrapperRef.current.clientWidth);
        }
        setDimensions((prevState) => {
          const newDimension = getDimension(width);

          return newDimension.width !== prevState.width || newDimension.height !== prevState.height
            ? newDimension
            : prevState;
        });
      };

      fn();

      parentSubscription.addEventListener('resize', fn);

      return () => {
        parentSubscription.removeEventListener('resize', fn);
      };
    }, [isInPopup, popupSize]);

    useImperativeHandle(ref, () => ({
      scrollTo: (index) => setIndexPosition(index),
    }));

    useEffect(() => {
      let showPrev = false;
      let showNext = false;

      const deltaCarouselSize = carouselWidth - wrapperWidth;

      if (deltaCarouselSize > 0 && !isMobileUserAgent) {
        showPrev = !!indexPosition;
        showNext = indexPosition * shiftSize < carouselWidth - wrapperWidth;
      }

      setPrevState(showPrev);
      setNextState(showNext);
      setTranslateShift(getNumberInRange(indexPosition * shiftSize, 0, Math.max(0, carouselWidth - wrapperWidth)));
    });

    const delayKeyPress = (event) => {
      if (event.key === KEY_TAB) {
        if (tabPressDelay.current) {
          event.preventDefault();
        } else {
          tabPressDelay.current = setTimeout(() => {
            tabPressDelay.current = null;
          }, TAB_PRESS_DELAY_TIME);
        }
      }
    };

    return (
      <div
        ref={wrapperRef}
        className={styles.Wrapper}
        onTouchStart={(event) =>
          setStart({
            x: event.touches[0].screenX,
            y: event.touches[0].screenY,
            canceled: false,
          })
        }
        onTouchMove={(event) => {
          if (!start.canceled) {
            setDraggableShiftPosition(event.touches[0].screenX - start.x);
            setAnimatedState(false);
          }
        }}
        onTouchEnd={(event) => {
          if (!start.canceled) {
            const delta = start.x - event.changedTouches[0].screenX;

            if (Math.abs(delta) >= TOUCH_SENSITIVE_CAROUSEL) {
              changeSlide(Math.sign(delta));
            }
          }

          setAnimatedState(true);
          setDraggableShiftPosition(0);
        }}
        onTouchCancel={() => {
          setAnimatedState(true);
          setDraggableShiftPosition(0);
        }}
        onKeyUp={delayKeyPress}
        onKeyDown={delayKeyPress}
        role="none"
      >
        <div
          ref={contentRef}
          className={classNames(styles.Content, {
            [styles.Content___animated]: animated,
          })}
          style={{
            gap: padding,
            width: carouselWidth,
            transform: `translate3d(${translateShift * -1 + draggableShiftPosition}px, 0, 0)`,
            paddingLeft: visibleIndexStart * shiftSize,
          }}
        >
          {children.map((video, index) => {
            if (!isInRange(index, visibleIndexStart, visibleIndexEnd)) {
              return null;
            }

            return (
              <div
                key={video.mediaid}
                className={styles.Slide}
                style={{
                  width: dimensions.width,
                }}
              >
                <Slide
                  isDraggable={isDraggable}
                  isActive={activeSlideMediaId === video.mediaid}
                  activeCustomContent={props.activeCustomContent}
                  video={video}
                  channel={props.channel}
                  width={dimensions.width}
                  height={dimensions.height}
                  onSlideClickFn={onSlideClickFn}
                  onSlideDoubleClickFn={onSlideDoubleClickFn}
                  onSlideFocusFn={() => setIndexPosition(Math.min(index, children.length - slidesToScroll))}
                  onEnded={props.onEnded}
                  preloadThumbnail
                  lazyLoading={false}
                />
              </div>
            );
          })}
        </div>
        <Arrow
          className={classNames(styles.Arrow, {
            [styles.Arrow___hidden]: !prevButton,
          })}
          height={dimensions.height}
          onClick={() => changeSlide(-1)}
        />
        <Arrow
          className={classNames(styles.Arrow, {
            [styles.Arrow___hidden]: !nextButton,
          })}
          height={dimensions.height}
          isNext
          onClick={() => changeSlide(1)}
        />
      </div>
    );
  },
);

CarouselWidget.displayName = 'CarouselWidget';

CarouselWidget.propTypes = {
  channel: channelPropTypes.isRequired,
  activeSlideMediaId: PropTypes.string,
  activeCustomContent: PropTypes.node,
  onSlideClickFn: PropTypes.func,
  onSlideDoubleClickFn: PropTypes.func,
  onEnded: PropTypes.func,
};

export const Skeleton = CarouselSkeleton;

export default CarouselWidget;
